import React from 'react';
import gql from 'graphql-tag';
import { useLazyQuery } from '@apollo/client';
import { Form as FForm, Formik } from 'formik';
import { Col, Form } from 'react-bootstrap';

import {
  ButtonSearch,
  FieldInput,
  FormCol6,
  promiseModalSelect,
  FormRow,
  TableCellV8,
  FilterProps,
} from 'motion-components';

// -------------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------------

interface JobTypeData {
  id: number;
  code: string;
  description: string;
  detailedDescription?: string;
  chargingCode?: string;
  materialId?: number;
  isActive: boolean;
  material?: {
    id: number;
    name: string;
  };
}

interface FieldJobTypeDlgProps {
  name: string;
  label?: string;
  disabled?: boolean;
  initFilters?: any;
  onSelect?: (jobType: JobTypeData) => void;
}

interface FormFieldJobTypeDlgProps extends FieldJobTypeDlgProps {
  as?: any;
  className?: string;
}

// -------------------------------------------------------------------------------
// Field Components
// -------------------------------------------------------------------------------

export const FieldJobTypeDlg: React.FC<FieldJobTypeDlgProps> = ({
  name,
  label = 'Τύπος Εργασίας',
  disabled = false,
  initFilters,
  onSelect,
  ...props
}) => {
  return (
    <FieldInput
      {...props}
      name={name}
      label={label}
      disabled={disabled}
      readOnly
      isSelectDialog
      selectDialogPromise={() =>
        promiseModalSelectJobType({
          initFilters,
          onSelect,
        })
      }
    />
  );
};

export const FormFieldJobTypeDlg: React.FC<FormFieldJobTypeDlgProps> = ({
  as: Component = FormCol6,
  className,
  ...props
}) => {
  return (
    <Component className={className}>
      <FieldJobTypeDlg {...props} />
    </Component>
  );
};

// -------------------------------------------------------------------------------
// GraphQL Query
// -------------------------------------------------------------------------------

const gqlGetJobTypes = gql`
  query getJobtypes($where: String, $offset: Int, $limit: Int, $order: String) {
    getJobtypes(where: $where, offset: $offset, limit: $limit, order: $order) {
      data {
        id
        code
        description
        isActive
        materialId
      }
      recordCount
    }
  }
`;

// -------------------------------------------------------------------------------
// Table Columns
// -------------------------------------------------------------------------------

const columns = [
  {
    id: 'code',
    header: 'Κωδικός',
    columnName: 'Κωδικός',
    accessorKey: 'code',
    orderByField: 'code',
    cell: TableCellV8,
    size: 80,
  },
  {
    id: 'description',
    header: 'Περιγραφή',
    columnName: 'Περιγραφή',
    accessorKey: 'description',
    orderByField: 'description',
    cell: TableCellV8,
    size: 160,
  },
  {
    id: 'isActive',
    header: 'Ενεργό',
    columnName: 'Ενεργό',
    accessorKey: 'isActive',
    orderByField: 'isActive',
    cell: (info) => {
      const isActive = info.getValue();
      return <div>{isActive ? 'Ναι' : 'Όχι'}</div>;
    },
    size: 60,
  },
];

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values) => {
  // Build the where clause for GraphQL
  let where = {};
  if (values) {
    if (values.searchText) {
      where = {
        ...where,
        $or: [
          { code: { $like: '%' + values.searchText + '%' } },
          { description: { $like: '%' + values.searchText + '%' } },
        ],
      };
    }
    if (values.isActive !== undefined && values.isActive !== null) {
      where = { ...where, isActive: values.isActive };
    }
  }
  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  return (
    <Formik
      initialValues={initialValues ? initialValues : { searchText: '', isActive: true }}
      onSubmit={onChange}
    >
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-end">
          <FieldInput
            as={FormCol6}
            name="searchText"
            id="searchText"
            label="Κωδικός ή Περιγραφή"
            type="search"
          />
          <Col className="ps-0 pe-0 me-3 ">
            <ButtonSearch size="sm" type="submit" className="float-end mb-2" />
          </Col>
        </FormRow>
      </Form>
    </Formik>
  );
};

// -------------------------------------------------------------------------------
// Modal Select Function
// -------------------------------------------------------------------------------

export const promiseModalSelectJobType = (props) => {
  return promiseModalSelect({
    ...props,
    title: 'Επιλογή Τύπου Εργασίας',
    columns,
    gqlQuery: gqlGetJobTypes,
    buildWhereFromFilters: buildWhereFromFilters,
    initFilters: props.initFilters,
    Filters: Filters,
  });
};

// -------------------------------------------------------------------------------
// Lazy Query Hook
// -------------------------------------------------------------------------------

export function useGetJobTypeById(id) {
  return useLazyQuery(gqlGetJobTypes, {
    variables: { where: JSON.stringify({ id: id }) },
    fetchPolicy: 'network-only',
  });
}

// -------------------------------------------------------------------------------
// Hook for Table Select
// -------------------------------------------------------------------------------

export function useGetJobTypesAsOptionsQuery() {
  return useLazyQuery(gqlGetJobTypes, {
    fetchPolicy: 'network-only',
  });
}

